import { X, ArrowLeft, Upload, CheckSquare, CreditCard } from 'lucide-react';
import { useState } from 'react';
import { addOrderToHistory } from '@/app/utils/orderHistory';

interface AlacarteCheckoutFormProps {
  onClose: () => void;
}

export function AlacarteCheckoutForm({ onClose }: AlacarteCheckoutFormProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [wantsAudition, setWantsAudition] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    objective: '',
    script: '',
    voiceSelection: '' as 'female' | 'male' | '',
    avatarSetting: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    mailingAddress: '',
    state: '',
    country: '',
    zipCode: '',
    upgradeScript: '',
    additionalScript: '',
  });
  const [portraitFile, setPortraitFile] = useState<File | null>(null);
  const [voiceSampleFile, setVoiceSampleFile] = useState<File | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleCardNumberChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 16) {
      handleInputChange('cardNumber', formatCardNumber(cleaned));
    }
  };

  const handleExpiryChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('expiryDate', formatExpiryDate(cleaned));
    }
  };

  const handleCvvChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('cvv', cleaned);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.objective || !formData.script || !formData.voiceSelection) {
      alert('Please fill in all required product details');
      return;
    }

    if (!agreedToTerms) {
      alert('Please agree to the Ethical and Moral Agreement');
      return;
    }

    if (paymentMethod === 'card' && (!formData.cardNumber || !formData.expiryDate || !formData.cvv || !formData.cardholderName || !formData.zipCode)) {
      alert('Please fill in all payment details');
      return;
    }

    // Word count validation for 20-30 word script
    const scriptWords = formData.script.trim().split(/\s+/).length;
    if (scriptWords < 20 || scriptWords > 30) {
      alert('Script must be between 20-30 words');
      return;
    }

    setIsProcessing(true);

    try {
      // TODO: Integrate with payment processor
      // TODO: Upload portrait to storage
      // TODO: Send order details to cara@oratf.info
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate transaction ID
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      
      // Prepare scripts with metadata
      const scriptsWithMetadata = [];
      const currentTimestamp = Date.now();
      
      // Add 20-30 word script
      if (formData.script) {
        scriptsWithMetadata.push({
          content: formData.script,
          submittedBy: formData.email,
          submittedAt: currentTimestamp,
          wordCount: formData.script.trim().split(/\s+/).filter(w => w).length
        });
      }
      
      // Add additional script if provided
      if (formData.additionalScript) {
        scriptsWithMetadata.push({
          content: formData.additionalScript,
          submittedBy: formData.email,
          submittedAt: currentTimestamp,
          wordCount: formData.additionalScript.trim().split(/\s+/).filter(w => w).length
        });
      }
      
      // Add upgrade script if provided
      if (formData.upgradeScript) {
        scriptsWithMetadata.push({
          content: formData.upgradeScript,
          submittedBy: formData.email,
          submittedAt: currentTimestamp,
          wordCount: formData.upgradeScript.trim().split(/\s+/).filter(w => w).length
        });
      }
      
      // Add order to history
      addOrderToHistory({
        userId: `USER-${Date.now()}`,
        userEmail: formData.email,
        orderType: 'alacarte',
        productName: 'Avatar Persona Video',
        amount: 24,
        status: 'pending',
        paymentMethod: paymentMethod,
        transactionId: transactionId,
        customerId: formData.email,
        scripts: scriptsWithMetadata,
        mediaType: portraitFile ? 'photo' : 'none',
        mediaUrl: portraitFile ? URL.createObjectURL(portraitFile) : undefined,
        voiceMediaUrl: voiceSampleFile ? URL.createObjectURL(voiceSampleFile) : undefined,
      });
      
      alert(`✅ Payment successful!\\n\\nYour avatar video order has been placed.\\n\\nA confirmation email has been sent to ${formData.email}.\\n\\nOur team will contact you within 24-48 hours to begin production.`);
      
      onClose();
    } catch (error: any) {
      alert(`Payment failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const wordCount = formData.script.trim().split(/\s+/).filter(w => w).length;
  const upgradeWordCount = formData.upgradeScript.trim().split(/\s+/).filter(w => w).length;
  const additionalWordCount = formData.additionalScript.trim().split(/\s+/).filter(w => w).length;
  
  // Check if user is using existing avatar via USEMYAVATAR code
  const isUsingExistingAvatar = formData.script.toUpperCase().includes('USEMYAVATAR');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-2">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl border border-gray-300 my-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-b from-purple-600 to-black p-2.5 rounded-t-xl relative">
          <button
            onClick={onClose}
            className="absolute top-1.5 left-1.5 text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
          >
            <ArrowLeft className="size-3.5" />
          </button>
          <button
            onClick={onClose}
            className="absolute top-1.5 right-1.5 text-white hover:bg-white/20 p-1 rounded-lg transition-colors"
          >
            <X className="size-3.5" />
          </button>
          <div className="text-center pt-2">
            <h2 className="text-sm font-bold text-white mb-0.5">Create Your Avatar Persona Video</h2>
            <p className="text-purple-100 text-[10px]">
              One-time payment • No subscription required
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-3 space-y-3">
          {/* Product Description */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-2.5">
            <h3 className="font-bold text-sm text-black mb-0.5">AI Pro Level 1</h3>
            <p className="text-[11px] text-green-600 font-semibold mb-1.5">No monthly commitment</p>
            <div className="space-y-0.5">
              <p className="text-xs font-bold text-black">Avatar Basic Template</p>
              <p className="text-base font-bold text-green-600">$24/one-time</p>
              <p className="text-[10px] text-gray-700">your template and introduction!</p>
              <ul className="text-[10px] space-y-0.5 mt-1">
                <li className="!text-black">• You script (20-30 words)</li>
                <li className="!text-black">• No revisions</li>
              </ul>
            </div>
          </div>

          {/* Avatar Name */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Avatar Name <span className="text-red-500">*</span> <span className="text-gray-500 text-[10px]">(about 7 or 8 memorable characters)</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="BIGMIKE2"
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Organization */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Organization <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => handleInputChange('organization', e.target.value)}
              placeholder="Your organization name"
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-sm"
            />
          </div>

          {/* Objective */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Objective <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.objective}
              onChange={(e) => handleInputChange('objective', e.target.value)}
              placeholder="Tell what you want viewers to learn or do."
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Script */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Script (20 - 30 words) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.script}
              onChange={(e) => handleInputChange('script', e.target.value)}
              placeholder="Your video will communicate this exact script so check it twice!"
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black resize-none h-24 text-sm"
              required
            />
            <div className={`text-[10px] mt-1 ${wordCount >= 20 && wordCount <= 30 ? 'text-green-600' : 'text-orange-600'}`}>
              {wordCount} words {wordCount < 20 ? '(minimum 20)' : wordCount > 30 ? '(maximum 30)' : '✓'}
            </div>
          </div>

          {/* Voice Selection */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Voice Selection (choose one) <span className="text-red-500">*</span>
            </label>
            <p className="text-[10px] text-gray-600 mb-2">These are Avatars - tap one to select the voice for your video.</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleInputChange('voiceSelection', 'female')}
                className={`p-2 border-2 rounded-lg transition-all ${
                  formData.voiceSelection === 'female'
                    ? 'border-orange-600 bg-orange-50'
                    : 'border-gray-300 bg-white hover:border-orange-400'
                }`}
              >
                <span className="text-[10px] font-semibold text-black">Female Voice</span>
              </button>
              <button
                type="button"
                onClick={() => handleInputChange('voiceSelection', 'male')}
                className={`p-2 border-2 rounded-lg transition-all ${
                  formData.voiceSelection === 'male'
                    ? 'border-orange-600 bg-orange-50'
                    : 'border-gray-300 bg-white hover:border-orange-400'
                }`}
              >
                <span className="text-[10px] font-semibold text-black">Male Voice</span>
              </button>
            </div>
          </div>

          {/* Voice Sample Upload */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Upload 30 Second Voice Sample <span className="text-gray-500 text-[10px]">(optional: when a sample voice file is not attached a voice is automatically assigned)</span>
            </label>
            <input
              type="file"
              accept="audio/*,video/mp4"
              onChange={(e) => setVoiceSampleFile(e.target.files?.[0] || null)}
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-xs file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-[10px] file:font-semibold file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
            />
            <p className="text-[10px] text-gray-600 mt-1">
              Upload a 30 second voice sample for voice cloning (MP3, MP4, WAV, M4A)
            </p>
            {voiceSampleFile && (
              <p className="text-[10px] text-green-600 mt-1">
                ✓ {voiceSampleFile.name}
              </p>
            )}
          </div>

          {/* Portrait Upload */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Portrait Upload {!isUsingExistingAvatar && <span className="text-red-500">*</span>}
              {isUsingExistingAvatar && <span className="text-gray-500 text-[10px]">(optional)</span>}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPortraitFile(e.target.files?.[0] || null)}
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-xs file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-[10px] file:font-semibold file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
              required={!isUsingExistingAvatar}
            />
            <p className="text-[10px] text-gray-600 mt-1">
              Upload a quality portrait photo facing forward in PNG, or JPG.
            </p>
            {isUsingExistingAvatar && (
              <p className="text-[10px] text-purple-600 mt-1 font-semibold">
                ✓ Using existing avatar - portrait upload is optional
              </p>
            )}
          </div>

          {/* Avatar Setting */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-1">
              Avatar Setting <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.avatarSetting}
              onChange={(e) => handleInputChange('avatarSetting', e.target.value)}
              placeholder="Describe the setting you want to see the avatar in (e.g., modern office, outdoor park, professional studio)"
              maxLength={256}
              rows={2}
              className="w-full px-2.5 py-1.5 border-2 border-gray-300 rounded-lg focus:border-orange-600 focus:outline-none text-black text-sm"
              required
            />
            <p className="text-[10px] mt-1 text-gray-600">
              {formData.avatarSetting.length}/256 characters
            </p>
          </div>

          {/* Ethical Agreement */}
          <div className="border-2 border-gray-300 rounded-lg p-2 bg-gray-50">
            <h3 className="font-bold text-xs text-black mb-1.5">Ethical and Moral Agreement</h3>
            <p className="text-[10px] text-gray-700 mb-2">
              Check the box to indicate you're 18 years of age or older. This video will be used for positive messaging and information integrity. You will not use this to cyber bully, intimidate, incite violence or share private information that could cause damages.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 size-3.5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                required
              />
              <span className="text-[10px] text-black">
                I agree to the Ethical and Moral Agreement <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* Payment Details */}
          <div className="border-t-2 border-orange-200 pt-2.5">
            <h3 className="font-bold text-xs text-black mb-2">Payment Details</h3>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                Processing Payment...
              </>
            ) : paymentMethod === 'paypal' ? (
              <>
                Pay with PayPal - $24
              </>
            ) : (
              <>
                Pay $24 Now
              </>
            )}
          </button>

          {/* Cancel Button */}
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-gray-400 hover:bg-gray-500 text-white font-bold py-2 rounded-lg transition-all text-sm"
          >
            Cancel
          </button>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-[10px] text-gray-600">
            <span className="text-base">🔒</span>
            <span>Secured by 256-bit SSL encryption</span>
          </div>
        </form>
      </div>
    </div>
  );
}